import { Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { ProgressStepper } from '@/components/shared/ProgressStepper'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { TaxRegistrationDetailed } from '@/types'
import { CreditCard, FileText, ChevronRight } from 'lucide-react'
import { FieldValues } from '@/components/applicant/FieldValues'
import type { FieldDefinition, FieldValueData } from '@/components/applicant/FieldValues'

const STAGE_KEYS = ['application', 'filing', 'completed'] as const
const STAGE_LABELS: Record<string, string> = {
  application: 'Application',
  filing: 'Filing',
  completed: 'Complete',
}


type Props = {
  registration: TaxRegistrationDetailed
  type: { id: string; name: string; gov_fee_cents: number; service_fee_cents: number }
  fields: FieldDefinition[]
  field_values: FieldValueData[]
}

export default function TaxRegistrationsShow({ registration, type, fields, field_values }: Props) {
  const basePath = useBasePath()
  const totalCost = type.gov_fee_cents + type.service_fee_cents

  return (
    <DashboardLayout title={registration.type_name} breadcrumbs={[{ label: 'Tax Registrations', href: 'tax_registrations' }, { label: registration.type_name }]}>
      <div>
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{registration.type_name}</h1>
            <StageBadge stage={registration.stage} variant="formation" />
          </div>
          <p className="text-sm text-muted-foreground">{registration.business_name} · {registration.jurisdiction_label}</p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/tax_registrations/${registration.id}/documents`}>
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Documents
                {registration.documents_count > 0 && (
                  <Badge variant="secondary" className="ml-1.5 font-normal text-xs px-1.5 py-0">{registration.documents_count}</Badge>
                )}
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/tax_registrations/${registration.id}/payment`}>
                <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                Payment
                {registration.payment?.paid && (
                  <Badge className="ml-1.5 font-normal text-xs px-1.5 py-0 bg-green-600 text-white">Paid</Badge>
                )}
              </Link>
            </Button>
          </div>
        </div>

        <div className="mb-8 py-4 px-5 border rounded-lg">
          <ProgressStepper stages={STAGE_KEYS} labels={STAGE_LABELS} currentStage={registration.stage} />
        </div>

        {(!registration.payment || !registration.payment.paid) && (
          <div className="mb-8">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Get Started</h2>
            <div className="rounded-lg border border-dashed bg-muted/30 divide-y divide-dashed">
              <Link href={`${basePath}/tax_registrations/${registration.id}/payment`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <CreditCard className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground">Complete payment</div>
                  <div className="text-xs text-muted-foreground">Pay {formatCurrency(totalCost)} to begin your registration</div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </Link>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Tax Number */}
          {registration.tax_number && (
            <section className="border rounded-lg px-4 py-3">
              <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Tax Number</p>
              <p className="text-sm font-mono">{registration.tax_number}</p>
            </section>
          )}

          {/* Form Fields */}
          {fields.length > 0 && (
            <section className="border rounded-lg px-4 py-3">
              <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-3">Form Data</p>
              <FieldValues fields={fields} values={field_values} />
            </section>
          )}

        </div>
      </div>
    </DashboardLayout>
  )
}
